import {
    Component,
    OnInit,
    Input,
    ViewChild,
    TemplateRef,
    HostListener,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { faPrint, faQuestion } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';

import { DateRange } from '../../models/date-range';
import { PcgTableColumn } from '../pcg-table-column.interface';
import { ButtonFilterService } from './button-filter/button-filter.service';
import { GlobalService } from '../../service/global.service';

@Component({
    selector: 'pcg-table-top',
    templateUrl: './table-top.component.html',
    styleUrls: ['./table-top.component.scss'],
    standalone: false,
})
export class TableTopComponent implements OnInit {
    @ViewChild('tooltipModal', { static: true }) tooltipModal: TemplateRef<any>;

    @Input() filterSource: BehaviorSubject<string>;
    @Input() columnDefs: Map<string, PcgTableColumn>;
    @Input() canGlobalSearch = true;
    @Input() canExactMatchSearch = true;
    @Input() canShowHide = true;
    @Input() canShowHideColumns = true;
    @Input() ajaxData: any;
    @Input() pageSizeSource: BehaviorSubject<number>;
    @Input() pageLengths: [number];
    @Input() table: any;
    @Input() excelUrl: string | null = null;
    @Input() customExcelFunc: () => void;
    @Input() canExportTableToExcel = false;
    @Input() canPrintTable = false;
    @Input() hasTooltips = false;
    @Input() tooltipContent: string;

    // Inputs for implementing the table dropdown filter features
    /** Filter ID to implement table dropdown filters. Passed from table component */
    @Input() filterId: string | null = null;
    /** Pass through a filtermap to show filters being utilized. Ex. src\app\shared\business-areas\order-list\order-list */
    @Input() filterMap: {} | null = null;
    /** Filters utilized for the table dropdown filters. Passed from table component */
    @Input() filters: any;
    /** Filter array for special reset button visibility behavior. Passed from table component */
    @Input() emptyFilters: {} | null = null;
    /** Whether the table dropdown filters have reset capabilities. Passed from table component*/
    @Input() canResetFilters = true;

    /* map of string and and function pointer key value pairs where the key is the key to the filter and the value is a function return a boolean of whether 
	or not to show reset filters*/
    @Input() customResetFiltersMap: Map<string, (args: any) => boolean>;

    subscriptions = new Subscription();

    // Get a unique id to append to the id of controls to make it distinct
    // Doing this to have a distinct id to add to the for attribute of the exact match label
    uniqueId = `${new Date().getDay()}-${new Date().getSeconds()}-${new Date().getMilliseconds()}`;

    faIconName = { faQuestion, faPrint };

    isMobile: boolean;
    @HostListener('window:resize') onResize() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth);
    }

    constructor(
        public btnFilterService: ButtonFilterService,
        public modalService: NgbModal,
        private httpClient: HttpClient
    ) {}

    ngOnInit() {
        if (this.canResetFilters) {
            this.emptyFilters ??= { ...this.filters };
        }
        this.isMobile = GlobalService.setIsMobile(window.innerWidth);
    }

    clearSearch(event) {
        setTimeout(() => {
            if (event.target.value == '') {
                this.filterSource.next('');
            }
        }, 1);
    }

    showResetFilterBtn() {
        if (this.customResetFiltersMap != null) {
            return (
                this.canResetFilters &&
                Object.entries(this.filters).filter(
                    ([key, val]) =>
                        this.customResetFiltersMap?.has(key) &&
                        this.customResetFiltersMap?.get(key)!(val)
                ).length
            );
        }

        return (
            this.canResetFilters &&
            Object.entries(this.filters).filter(
                ([key, val]) =>
                    key in this.emptyFilters! &&
                    val !== false &&
                    !(
                        this.emptyFilters![key] === val ||
                        // Checking dateranges due to clearing range sets value to an object.
                        (this.emptyFilters![key] instanceof DateRange &&
                            this.emptyFilters![key].dateBegin ===
                                (val!['dateBegin'] ?? null) &&
                            this.emptyFilters![key].dateEnd ===
                                (val!['dateEnd'] ?? null))
                    )
            ).length
        );
    }

    tooltipButtonClicked() {
        this.modalService.open(this.tooltipModal);
    }

    getExcel = () =>
        this.customExcelFunc != null
            ? this.customExcelFunc.call(this)
            : this.excelUrl
              ? this.subscriptions.add(
                    this.httpClient
                        .get(this.excelUrl)
                        .subscribe((output: any) => {
                            window.open(output);
                        })
                )
              : this.table.getExcel();
}
